import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { ChevronDown, Plus, X, Variable } from "lucide-react";

/**
 * Reusable variable dropdown component.
 * Shows all flow variables, allows selection, and optionally inline creation.
 *
 * The dropdown menu is rendered via a React Portal into document.body so it
 * is never clipped by ancestor overflow:hidden / overflow:auto containers.
 *
 * @param {string}   value          – currently selected variable name
 * @param {Function} onChange       – called with variable name on selection
 * @param {Array}    flowVariables  – [{ id, name, description }]
 * @param {boolean}  allowCreate    – show inline "create new" option
 * @param {Function} onCreateVariable – called with new variable name
 * @param {string}   placeholder    – placeholder text
 * @param {string}   className      – additional wrapper classes
 */
const VariableDropdown = ({
  value,
  onChange,
  flowVariables = [],
  allowCreate = false,
  onCreateVariable,
  placeholder = "Select variable...",
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState("");
  const [pos, setPos] = useState({ top: 0, left: 0, width: 200 });

  const triggerRef = useRef(null); // the wrapper div (also used for trigger rect)
  const portalRef = useRef(null); // the portaled dropdown div

  // ── Compute position when opening ──
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 200),
    });
  }, []);

  // When open becomes true, calculate position
  useEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  // ── Close on outside click (works across the portal boundary) ──
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const inTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      const inPortal = portalRef.current && portalRef.current.contains(e.target);
      if (!inTrigger && !inPortal) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [open]);

  // ── Close on scroll or resize (outside menu only) so the menu repositions / doesn't float orphaned ──
  useEffect(() => {
    if (!open) return;
    const onScroll = (e) => {
      const el = e.target;
      if (portalRef.current && el instanceof Node && portalRef.current.contains(el)) {
        return; // scrolling inside the variable list — keep open
      }
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  const normalizeVariableEntry = (v) => {
    if (!v) return null;
    if (typeof v === "string") {
      const name = v.trim();
      return name ? { id: name, name, description: "" } : null;
    }
    const name = String(v.name ?? "").trim();
    return name ? { id: v.id || name, name, description: v.description || "" } : null;
  };

  const safeVariables = (flowVariables || []).map(normalizeVariableEntry).filter(Boolean);

  const filtered = safeVariables.filter((v) =>
    v.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (name) => {
    onChange(name);
    setOpen(false);
    setSearch("");
  };

  const handleCreate = () => {
    const trimmed = newName.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (!trimmed) {
      setError("Name required");
      return;
    }
    if (flowVariables.some((v) => {
      const name = typeof v === "string" ? v.trim() : String(v?.name ?? "").trim();
      return name === trimmed;
    })) {
      setError("Already exists");
      return;
    }
    if (onCreateVariable) onCreateVariable(trimmed);
    onChange(trimmed);
    setCreating(false);
    setNewName("");
    setError("");
    setOpen(false);
  };

  // ── Dropdown content (rendered in portal) ──
  const dropdownContent = (
    <div
      ref={portalRef}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        width: pos.width,
        minWidth: 200,
        zIndex: 99999,
      }}
      className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden flex flex-col max-h-48"
      onWheel={(e) => e.stopPropagation()}
    >
      {/* Search */}
      <div className="p-1.5 border-b border-gray-100 flex-shrink-0">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search variables..."
          className="w-full px-2 py-1 text-[10px] bg-[#F0F0F0] rounded border-0 focus:outline-none"
          autoFocus
        />
      </div>

      {/* List — min-h-0 so flex child can shrink and overflow-y-auto actually scrolls */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {/* None option */}
        <button
          type="button"
          onClick={() => handleSelect("")}
          className="w-full px-2.5 py-1.5 text-left text-[10px] text-[#8696a0] hover:bg-gray-50 italic"
        >
          None
        </button>

        {filtered.map((v) => (
          <button
            key={v.id || v.name}
            type="button"
            onClick={() => handleSelect(v.name)}
            className={`w-full px-2.5 py-1.5 text-left hover:bg-[#E7FCE8] flex items-center gap-1.5 ${
              value === v.name ? "bg-[#E7FCE8]" : ""
            }`}
          >
            <Variable size={10} className="text-[#008069] flex-shrink-0" />
            <span className="text-[10px] font-mono font-semibold text-[#008069]">{v.name}</span>
            {v.description && (
              <span className="text-[9px] text-[#8696a0] truncate ml-auto">{v.description}</span>
            )}
          </button>
        ))}

        {filtered.length === 0 && !creating && (
          <p className="px-2.5 py-2 text-[10px] text-[#8696a0] text-center">No variables found</p>
        )}
      </div>

      {/* Create inline */}
      {allowCreate && (
        <div className="border-t border-gray-100 p-1.5 flex-shrink-0">
          {!creating ? (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="w-full flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-[#008069] hover:bg-[#E7FCE8] rounded"
            >
              <Plus size={10} />
              Create Variable
            </button>
          ) : (
            <div className="space-y-1">
              <div className="flex gap-1">
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value);
                    setError("");
                  }}
                  placeholder="variable_name"
                  className="flex-1 px-2 py-1 text-[10px] font-mono bg-[#F0F0F0] rounded border-0 focus:outline-none"
                  autoFocus
                  onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                />
                <button
                  type="button"
                  onClick={handleCreate}
                  className="px-1.5 py-1 bg-[#008069] text-white rounded text-[9px] font-bold"
                >
                  Add
                </button>
                <button
                  type="button"
                  onClick={() => { setCreating(false); setNewName(""); setError(""); }}
                  className="p-1 text-[#667781] hover:text-red-500"
                >
                  <X size={10} />
                </button>
              </div>
              {error && <p className="text-[9px] text-red-500 font-medium">{error}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div ref={triggerRef} className={`relative ${className}`}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-1 px-2 py-1.5 text-[11px] bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-[#008069]/30 text-left"
      >
        {value ? (
          <span className="font-mono font-semibold text-[#008069] truncate">{`{{${value}}}`}</span>
        ) : (
          <span className="text-[#8696a0]">{placeholder}</span>
        )}
        <ChevronDown size={12} className="text-[#667781] flex-shrink-0" />
      </button>

      {/* Dropdown – rendered via portal so it escapes overflow:hidden ancestors */}
      {open && ReactDOM.createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default VariableDropdown;
